/**
 * Email Delivery Status Badge - Phase 10a.3
 *
 * Color-coded badge showing email delivery status from Mailgun webhooks.
 * Statuses: pending (yellow), delivered (green), bounced (red), failed (red)
 */
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { CheckCircle2, Clock, XCircle, AlertTriangle } from 'lucide-react';

type DeliveryStatus = 'pending' | 'delivered' | 'bounced' | 'failed';

interface EmailDeliveryStatusBadgeProps {
  status: DeliveryStatus;
  className?: string;
}

const statusConfig: Record<DeliveryStatus, {
  label: string;
  icon: typeof CheckCircle2;
  variant: 'default' | 'secondary' | 'outline' | 'destructive';
  className: string;
}> = {
  pending: {
    label: 'Pending',
    icon: Clock,
    variant: 'secondary',
    className: 'bg-yellow-100 text-yellow-800',
  },
  delivered: {
    label: 'Delivered',
    icon: CheckCircle2,
    variant: 'secondary',
    className: 'bg-green-100 text-green-800',
  },
  bounced: {
    label: 'Bounced',
    icon: AlertTriangle,
    variant: 'destructive',
    className: '',
  },
  failed: {
    label: 'Failed',
    icon: XCircle,
    variant: 'destructive',
    className: '',
  },
};

export function EmailDeliveryStatusBadge({ status, className }: EmailDeliveryStatusBadgeProps) {
  const config = statusConfig[status] || statusConfig.pending;
  const Icon = config.icon;

  return (
    <Badge
      variant={config.variant}
      className={cn('inline-flex items-center gap-1', config.className, className)}
      data-testid={`delivery-status-${status}`}
    >
      <Icon className="w-3 h-3" />
      {config.label}
    </Badge>
  );
}
